// PricingPlans: Display subscription plans with Alex Hermozi copywriting framework
// Optimized for high conversion with social proof and urgency elements

import React, { useState, useEffect } from 'react'
import { useSubscription } from '@/hooks/useSubscription'
import { useAuth } from '@/hooks/useAuth'
import { PricingPlanSkeleton } from './SkeletonLoader'
// import { useAnalytics } from '../../hooks/useAnalytics'
import { STRIPE_PRICES, validateStripeConfig } from '../../config/stripe-prices'
import type { SubscriptionPlan, CreateCheckoutRequest } from '@/types/billing'

interface PricingPlansProps {
  className?: string
  highlightPlan?: string
  showTrialBanner?: boolean
  currentPlanId?: string // User's current plan ID to show indicators
  onPlanSelect?: (plan: SubscriptionPlan, billingCycle: 'monthly' | 'yearly') => void
}

export function PricingPlans({
  className = '',
  highlightPlan = 'professional',
  showTrialBanner = true,
  currentPlanId,
  onPlanSelect
}: PricingPlansProps) {
  const { plans, isLoading, error, createCheckoutSession } = useSubscription()
  const { user } = useAuth()
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly')
  const [isProcessing, setIsProcessing] = useState<string | null>(null)
  const [stripeConfigError, setStripeConfigError] = useState<string | null>(null)
  // const { trackButtonClick, trackBilling } = useAnalytics()

  // Validate Stripe configuration on mount
  useEffect(() => {
    const validation = validateStripeConfig()
    if (!validation.isValid) {
      const message = `Stripe configuration incomplete. Missing price IDs: ${validation.missingIds.join(', ')}`
      setStripeConfigError(message)
      console.warn('⚠️ Stripe Configuration Warning:', message)
    }
  }, [])

  if (isLoading) {
    return (
      <div className={`py-12 ${className}`}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="space-y-8">
            <div className="text-center space-y-4">
              <div className="h-8 bg-gray-200 rounded w-64 mx-auto animate-pulse"></div>
              <div className="h-4 bg-gray-200 rounded w-96 mx-auto animate-pulse"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <PricingPlanSkeleton key={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Debug plans loading
  console.log('🔍 PricingPlans - Plans loaded:', {
    planCount: plans.length,
    error,
    plans: plans.map(p => ({ name: p.name, id: p.id }))
  })

  if (error || !plans.length) {
    console.log('🔍 PricingPlans - Error or no plans:', { error, planCount: plans.length })
    return (
      <div className={`py-12 ${className}`}>
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="text-red-600">
            Unable to load pricing plans. Please try again later.
            {error && <div className="mt-2 text-sm">Error: {error}</div>}
          </div>
        </div>
      </div>
    )
  }

  const handlePlanSelect = async (plan: SubscriptionPlan) => {
    // Track plan selection
    // trackButtonClick(`select_plan_${plan.name.toLowerCase()}`, 'pricing_plans')
    // trackBilling('plan_selected', plan.id, plan.price_monthly_cents)

    if (onPlanSelect) {
      onPlanSelect(plan, billingCycle)
      return
    }

    // Direct to Stripe Checkout - no authentication required for better conversion
    setIsProcessing(plan.id)
    try {
      // Map plan to actual Stripe price IDs using STRIPE_PRICES config
      const planName = plan.name.toLowerCase() as keyof typeof STRIPE_PRICES
      const stripePlan = STRIPE_PRICES[planName]

      if (!stripePlan) {
        throw new Error(`No Stripe configuration found for plan: ${plan.name}`)
      }

      const priceId = billingCycle === 'yearly' ? stripePlan.annual : stripePlan.monthly

      console.log('🔍 Plan data:', { name: plan.name, id: plan.id, planName, stripePlan })
      console.log('🔍 Resolved price ID:', priceId, 'for billing cycle:', billingCycle)

      if (!priceId) {
        throw new Error(`No price ID found for ${plan.name} ${billingCycle}`)
      }

      // Different success URLs for authenticated vs guest users
      // Authenticated users (upgrading) → Show PaymentSuccess modal
      // Guest users (paying before signup) → Show CreateAccountAfterPayment flow
      const successUrl = user
        ? `${window.location.origin}/?success=true&session_id={CHECKOUT_SESSION_ID}`
        : `${window.location.origin}/create-account?session_id={CHECKOUT_SESSION_ID}`

      console.log('🔍 Creating checkout session')
      console.log('🔍 User authenticated:', !!user)
      console.log('🔍 Success URL:', successUrl)
      console.log('🔍 Final request will use price_id:', priceId)

      const request: CreateCheckoutRequest = {
        price_id: priceId,
        billing_cycle: billingCycle === 'yearly' ? 'annual' : 'monthly',
        success_url: successUrl,
        cancel_url: window.location.href
      }

      console.log('🔍 Full checkout request:', request)
      console.log('🔍 Success URL being sent to API:', request.success_url)
      const session = await createCheckoutSession(request)
      console.log('🔍 Stripe session created:', session)
      window.location.href = session.checkout_url
    } catch (err) {
      console.error('Failed to create checkout session:', err)
      setIsProcessing(null)

      // Show user-friendly error message
      const errorMessage = err instanceof Error ? err.message : 'Failed to create checkout session'
      alert(`Payment Error: ${errorMessage}`)
    }
  }

  const getYearlySavings = (plan: SubscriptionPlan) => {
    if (!plan.price_monthly_cents || !plan.price_annual_cents) return 0
    const monthlyTotal = plan.price_monthly_cents * 12
    const yearlySavings = monthlyTotal - plan.price_annual_cents
    return Math.round((yearlySavings / monthlyTotal) * 100)
  }

  const formatPrice = (price: number | null) => {
    if (!price) return 'Custom'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price / 100)
  }

  const getDisplayPrice = (plan: SubscriptionPlan) => {
    const price = billingCycle === 'yearly' ? plan.price_annual_cents : plan.price_monthly_cents
    return formatPrice(price)
  }

  const isPlanHighlighted = (plan: SubscriptionPlan) => {
    return plan.name.toLowerCase() === highlightPlan.toLowerCase()
  }

  const isPlanCurrent = (plan: SubscriptionPlan) => {
    return currentPlanId && plan.id === currentPlanId
  }

  return (
    <div className={`py-12 ${className}`}>
      <div className="max-w-6xl mx-auto px-4">
        {/* Header Section - Alex Hermozi Style */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Finally, AI That Actually Saves You Time
          </h2>
          <p className="text-xl text-gray-600 mb-6 max-w-3xl mx-auto">
            Stop wasting hours writing metadata. Our AI tags 1000+ images in minutes, 
            not days. Join 2,847+ creators who've already made the switch.
          </p>
          
          {showTrialBanner && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8 max-w-md mx-auto">
              <p className="text-green-800 font-medium">
                🎉 Start with 50 FREE credits - No card required
              </p>
            </div>
          )}

          {/* Billing Toggle */}
          <div className="flex items-center justify-center space-x-4 mb-8">
            <span className={`font-medium ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-500'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                billingCycle === 'yearly' ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`font-medium ${billingCycle === 'yearly' ? 'text-gray-900' : 'text-gray-500'}`}>
              Yearly
            </span>
            <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded-full">
              Save up to 25%
            </span>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {plans.map((plan) => {
            const isHighlighted = isPlanHighlighted(plan)
            const isCurrent = isPlanCurrent(plan)
            const yearlySavings = getYearlySavings(plan)

            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl border-2 p-8 ${
                  isCurrent
                    ? 'border-green-500 shadow-xl scale-105 bg-green-50'
                    : isHighlighted
                    ? 'border-blue-500 shadow-xl scale-105'
                    : 'border-gray-200 shadow-lg'
                }`}
              >
                {isCurrent && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                      Current Plan
                    </span>
                  </div>
                )}
                {isHighlighted && !isCurrent && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}

                {billingCycle === 'yearly' && yearlySavings > 0 && (
                  <div className="absolute -top-2 -right-2">
                    <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                      Save {yearlySavings}%
                    </span>
                  </div>
                )}

                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-gray-600 mb-6 min-h-[48px]">
                    {plan.description}
                  </p>

                  <div className="mb-6">
                    <span className="text-5xl font-bold text-gray-900">
                      {getDisplayPrice(plan)}
                    </span>
                    {plan.price_monthly_cents && (
                      <span className="text-gray-600 ml-2">
                        /{billingCycle === 'yearly' ? 'year' : 'month'}
                      </span>
                    )}
                  </div>

                  <button
                    onClick={() => !isCurrent && handlePlanSelect(plan)}
                    disabled={isProcessing === plan.id || isCurrent}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all ${
                      isCurrent
                        ? 'bg-green-100 text-green-800 border border-green-300 cursor-not-allowed'
                        : isHighlighted
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    } ${
                      isProcessing === plan.id ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isCurrent ? 'Current Plan' : isProcessing === plan.id ? 'Processing...' : 'Start Now'}
                  </button>
                </div>

                <div className="mt-8 space-y-4">
                  <div className="text-sm font-medium text-gray-900 border-b pb-2">
                    What's included:
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm text-gray-600">
                        {plan.credits_per_month} credits/month
                      </span>
                    </div>
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm text-gray-600">
                        AI title & description generation
                      </span>
                    </div>
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm text-gray-600">
                        Keyword & category tagging
                      </span>
                    </div>
                    {plan.name !== 'Starter' && (
                      <>
                        <div className="flex items-center">
                          <svg className="h-5 w-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm text-gray-600">
                            Batch processing
                          </span>
                        </div>
                        <div className="flex items-center">
                          <svg className="h-5 w-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm text-gray-600">
                            Priority support
                          </span>
                        </div>
                      </>
                    )}
                    {plan.name === 'Business' && (
                      <div className="flex items-center">
                        <svg className="h-5 w-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm text-gray-600">
                          API access & white-label
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Social Proof Section */}
        <div className="text-center">
          <p className="text-gray-600 mb-4">
            Trusted by 2,847+ content creators and agencies
          </p>
          <div className="flex justify-center items-center space-x-8 opacity-60">
            <span className="text-sm font-medium">Stock Photography</span>
            <span className="text-sm font-medium">Digital Agencies</span>
            <span className="text-sm font-medium">E-commerce</span>
            <span className="text-sm font-medium">Social Media</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PricingPlans